// Release C-2 (P5) · Owner Notification Preferences page.
// Owner-only route: /admin/settings/owner-preferences.
// SMS section shows "Awaiting Twilio activation" until Twilio is turned on.
// Preferences NEVER suppress an Owner Inbox row (enforced server-side).
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const EMAIL_EVENTS = [
  { key: "new_lead", label: "New lead" },
  { key: "new_access_request", label: "New portal access request" },
  { key: "new_questionnaire", label: "New questionnaire response" },
  { key: "delivery_failure", label: "Delivery failure" },
  { key: "portal_switch", label: "Portal switch" },
  { key: "credential_expiring", label: "Credential expiring" },
  { key: "approval_pending", label: "Approval pending" },
];

const TILE_KEYS = [
  ["owner_inbox", "Owner Inbox"],
  ["delivery_failures", "Delivery failures"],
  ["portal_access", "Portal access"],
  ["communications", "Communications"],
  ["credentials", "Credentials"],
  ["audit_ledger", "Audit ledger"],
];

export default function OwnerPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [smsState, setSmsState] = useState("NOT_CONFIGURED");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/owner/preferences");
      setPrefs(r.data.preferences);
      setSmsState(r.data.sms_provider_state || "NOT_CONFIGURED");
    } catch (e) {
      toast.error("Failed to load preferences");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const p = { ...prefs, events_sms: [] };  // SMS is Twilio-gated
      await api.post("/owner/preferences", p);
      toast.success("Preferences saved");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Save failed");
    } finally { setBusy(false); }
  };

  if (!prefs) return (
    <AdminAppShell current="/admin/settings/owner-preferences">
      <div style={{ padding: 24, color: "#64748b" }}>Loading preferences…</div>
    </AdminAppShell>
  );

  const toggleEvent = (key) => {
    const has = prefs.events_email.includes(key);
    setPrefs({ ...prefs,
      events_email: has ? prefs.events_email.filter((k) => k !== key)
                        : [...prefs.events_email, key] });
  };

  const toggleTile = (key) => {
    setPrefs({ ...prefs,
      tile_visibility: { ...prefs.tile_visibility,
                          [key]: !prefs.tile_visibility[key] } });
  };

  return (
    <AdminAppShell current="/admin/settings/owner-preferences">
      <div data-testid="owner-preferences-page" style={{ padding: 24, maxWidth: 900 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Owner Notification Preferences</h1>
        <div style={{ color: "#475569", fontSize: 13, marginTop: 4, marginBottom: 20 }}>
          Preferences <b>never suppress an Owner Inbox row</b> — they only affect the outbound notification path.
        </div>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Events → Email</div>
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {EMAIL_EVENTS.map((e) => (
                <label key={e.key} data-testid={`pref-email-${e.key}`} style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={prefs.events_email.includes(e.key)}
                    onChange={() => toggleEvent(e.key)}
                  />
                  {e.label}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Events → SMS</div>
            <div style={{ marginTop: 8, fontSize: 13, color: "#475569" }}>
              <span data-testid="pref-sms-chip" style={{
                display: "inline-block", padding: "3px 10px", borderRadius: 9999,
                background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 600, marginRight: 8,
              }}>Awaiting Twilio activation</span>
              SMS event routing unlocks after Twilio is provisioned. No SMS events can be enabled until then.
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Digest mode</div>
              <select
                data-testid="pref-digest-mode"
                value={prefs.digest_mode}
                onChange={(e) => setPrefs({ ...prefs, digest_mode: e.target.value })}
                style={{ marginTop: 6, padding: 8, borderRadius: 4, border: "1px solid #cbd5e1", fontSize: 13, width: "100%" }}
              >
                <option value="immediate">Immediate</option>
                <option value="hourly">Hourly digest</option>
                <option value="daily">Daily digest</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Escalation (minutes)</div>
              <Input
                data-testid="pref-escalation-minutes"
                type="number"
                value={prefs.escalation?.after_minutes ?? 30}
                onChange={(e) => setPrefs({ ...prefs,
                  escalation: { ...prefs.escalation,
                                 after_minutes: Number(e.target.value) } })}
                style={{ marginTop: 6 }}
              />
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Quiet hours start</div>
              <Input
                data-testid="pref-quiet-start"
                value={prefs.quiet_hours?.start || "22:00"}
                onChange={(e) => setPrefs({ ...prefs,
                  quiet_hours: { ...prefs.quiet_hours, start: e.target.value } })}
                style={{ marginTop: 6 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Quiet hours end</div>
              <Input
                data-testid="pref-quiet-end"
                value={prefs.quiet_hours?.end || "07:00"}
                onChange={(e) => setPrefs({ ...prefs,
                  quiet_hours: { ...prefs.quiet_hours, end: e.target.value } })}
                style={{ marginTop: 6 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Timezone</div>
              <Input
                data-testid="pref-quiet-tz"
                value={prefs.quiet_hours?.timezone || "America/New_York"}
                onChange={(e) => setPrefs({ ...prefs,
                  quiet_hours: { ...prefs.quiet_hours, timezone: e.target.value } })}
                style={{ marginTop: 6 }}
              />
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Backup recipient</div>
            <Input
              data-testid="pref-backup-recipient"
              value={prefs.backup_recipient || ""}
              placeholder="assistant@erfenterprisesllc.com"
              onChange={(e) => setPrefs({ ...prefs, backup_recipient: e.target.value })}
              style={{ marginTop: 6, maxWidth: 400 }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
              Optional. Receives a copy of every outbound notification you would receive.
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Tile visibility</div>
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {TILE_KEYS.map(([k, label]) => (
                <label key={k} data-testid={`pref-tile-${k}`} style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={prefs.tile_visibility?.[k] ?? true}
                    onChange={() => toggleTile(k)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <div style={{ display: "flex", gap: 8 }}>
          <Button data-testid="pref-save" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save preferences"}
          </Button>
          <Button variant="outline" onClick={load} disabled={busy}>Reset</Button>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#64748b" }}>
          Owner Inbox rows are always written to <code>owner_inbox_items</code>, regardless of these preferences.
        </div>
      </div>
    </AdminAppShell>
  );
}
